import {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';

import {useTranslation} from 'react-i18next';
import {useLocation, useNavigate} from 'react-router-dom';

import {
  STORAGE_KYC_ADDRESSES_FORM_FIELDS,
  STORAGE_KYC_FULL_ADDRESS,
} from '@/app/auth/constants/KycConstants';
import {KYC_API_EVENTS} from '@/app/auth/events/KycApiEvents';
import {useAuthFunctions} from '@/app/auth/hooks/AuthFunctions';
import {DEFAULT_CARD} from '@/app/main/onboarding/constants/OnboardingConstants';
import {getKidData} from '@/app/main/onboarding/pages/AddKid/services/handleKidInformation';
import {showSnackbar} from '@/app/shared-components/Snackbar/SnackbarProvider';
import {SnackbarType} from '@/app/shared-components/Snackbar/SnackbarTypes';
import {
  isValidPersistedAddress,
  normalizePersistedAddress,
} from '@/app/utils/KycAddressUtils';
import {useLoading} from '@/app/zustand/useLoading';
import {
  C_KYC_IN_REVIEW_SUB_STATES,
  C_StatusKYC_2,
  TStatusKYC2,
} from '@/constants/KycConstants';
import {AddressFormKyc} from '@/dataSource/src/api/kyc/useRepository';
import {useUsersRepository} from '@/dataSource/src/api/users/useRepository';
import {
  IKycWebSocketData,
  useWebSocket,
} from '@/services/socket/useWebSocketService';
import {WEB_VIEW_MESSAGES} from '@/services/webview/constants';
import {WebViewService} from '@/services/webview/WebViewService';
import {getFromStorage} from '@/utils/getFromStorage';

import {useTrackers} from './trackers';

type ViewModelContextType = {
  data: IKycWebSocketData | null;
  isConnected: boolean;
  sendMessage: (message: any) => void;
  disconnect: () => void;
};

const ViewModelContext = createContext<ViewModelContextType | undefined>(
  undefined,
);

export function ViewModelProvider({children}: {children: ReactNode}) {
  const [data, setData] = useState<IKycWebSocketData | null>(null);
  const {stopLoading} = useLoading();
  const navigate = useNavigate();
  const location = useLocation();
  const {t} = useTranslation();
  const {trackApiError, trackApiSuccess} = useTrackers();
  const {logout} = useAuthFunctions();
  const {profile} = useUsersRepository();
  const kidData = getKidData();
  const selectedTheme =
    profile?.metadata?.ONBOARDING_CARD_THEME_SELECTED || DEFAULT_CARD;

  const handleSsnBlocked = useCallback(async () => {
    const title = t('kyc.ssn_blocked.summary');
    const message = t('kyc.ssn_blocked.message');
    showSnackbar({
      summary: title,
      message: message,
      variant: SnackbarType.ERROR,
      autoHideDuration: 10000,
    });
    await logout(true, {
      kycStatus: C_StatusKYC_2.SSN_BLOCKED,
      customTitle: title,
      customMessage: message,
    });
  }, [logout, t]);

  const isSsnBlocked = useCallback((kycCurrentState: TStatusKYC2) => {
    return kycCurrentState === C_StatusKYC_2.SSN_BLOCKED;
  }, []);

  const isWebViewActive = useMemo(() => {
    return (
      WebViewService.isWebViewActive() &&
      typeof window.ReactNativeWebView !== 'undefined' &&
      window.ReactNativeWebView
    );
  }, []);

  const previousScreen = useMemo(() => {
    const pathnameParts = location.pathname.split('/').filter(Boolean);
    return pathnameParts[pathnameParts.length - 1] ?? '';
  }, [location.pathname]);

  const handleSendWebViewMessage = useCallback(() => {
    stopLoading();
    window.ReactNativeWebView.postMessage(
      JSON.stringify({
        status: WEB_VIEW_MESSAGES.CONTINUE_ONBOARDING,
        kidUuid: kidData?.uuid,
        choseDesign: selectedTheme,
        previousScreen: 'kyc viewModelContext approved',
      }),
    );
  }, [kidData?.uuid, selectedTheme, stopLoading]);

  // Use WebSocket hook for managing the connection
  const {
    isConnected,
    sendMessage,
    disconnect,
    data: dataWebSocket,
  } = useWebSocket({
    onMessage: event => {
      const parsedData: IKycWebSocketData = JSON.parse(event.data);

      if (isSsnBlocked(parsedData.kycCurrentState)) {
        handleSsnBlocked();
        return;
      }

      if (!parsedData || !parsedData.kycCurrentState) {
        setData({
          kycCurrentState: C_StatusKYC_2.NONE,
          subStateInReview: C_KYC_IN_REVIEW_SUB_STATES.NONE,
        });
      } else {
        setData(parsedData);
      }
    },
    onError: error => {
      trackApiError(KYC_API_EVENTS.API_KYC_WEBSOCKET_GET_DATA_ERROR, error);
    },
    onOpen: () => {
      console.log('WebSocket connected');
    },
    onClose: () => {
      console.log('WebSocket disconnected');
    },
  });

  // Simulate fetching token and version
  useEffect(() => {
    return () => {
      disconnect();
    };
  }, [disconnect]);

  const updateStatusKYC = useCallback(
    (data: IKycWebSocketData) => {
      if (!data) {
        trackApiSuccess(KYC_API_EVENTS.API_KYC_WEBSOCKET_GET_DATA_WITHOUT_DATA);
        return;
      }

      if (isSsnBlocked(data.kycCurrentState)) {
        handleSsnBlocked();
        return;
      }

      setData(data);

      if (data.kycCurrentState) {
        trackApiSuccess(KYC_API_EVENTS.API_KYC_WEBSOCKET_GET_DATA_SUCCESS, {
          user: {
            kyc: {
              state: data.kycCurrentState,
              idv_state: data.subStateInReview,
            },
          },
        });
      }

      const normalizedKycCurrentState =
        data.kycCurrentState || C_StatusKYC_2.NONE;

      switch (normalizedKycCurrentState) {
        case C_StatusKYC_2.KYC_NOT_STARTED:
        case C_StatusKYC_2.NONE: {
          const persistedAddressFromFields = getFromStorage<AddressFormKyc>(
            STORAGE_KYC_ADDRESSES_FORM_FIELDS,
            localStorage,
          );
          const persistedAddressFromFullAddress =
            getFromStorage<AddressFormKyc>(
              STORAGE_KYC_FULL_ADDRESS,
              localStorage,
            );
          const persistedAddress = normalizePersistedAddress(
            persistedAddressFromFields || persistedAddressFromFullAddress,
          );

          if (isValidPersistedAddress(persistedAddress)) {
            navigate('/onboarding/kyc/plaid', {
              replace: true,
              state: {
                formAddress: persistedAddress,
              },
            });
            break;
          }

          // The source-of-funds screen is shown before the address screen.
          navigate('/onboarding/kyc/source-of-funds');
          break;
        }
        case C_StatusKYC_2.KYC_STARTED:
        case C_StatusKYC_2.KYC_SUBMITTED:
          navigate('/onboarding/kyc/submitted');
          break;
        case C_StatusKYC_2.KYC_APPROVED:
          if (isWebViewActive) {
            handleSendWebViewMessage();
          } else {
            navigate('/onboarding/kyc/approved');
          }
          break;
        case C_StatusKYC_2.KYC_DECLINED:
          setTimeout(() => {
            return navigate('/onboarding/kyc/declined');
          }, 100);
          break;
        case C_StatusKYC_2.KYC_MANUALLY_REVIEW:
          navigate(
            `/onboarding/your-info-is-in-review?previousScreen=${encodeURIComponent(previousScreen)}`,
          );
          break;
        case C_StatusKYC_2.KYC_IN_REVIEW:
          if (data.subStateInReview) {
            console.log(`Sub-state in review: ${data.subStateInReview}`);
          }
          if (
            data.subStateInReview ===
            C_KYC_IN_REVIEW_SUB_STATES.MANUAL_REVIEW_REQUIRED
          ) {
            setTimeout(() => {
              return navigate(
                `/onboarding/your-info-is-in-review?previousScreen=${encodeURIComponent(previousScreen)}`,
              );
            }, 100);
          } else {
            setTimeout(() => {
              return navigate('/onboarding/kyc/in-review');
            }, 100);
          }

          break;
        default:
          // The source-of-funds screen is shown before the address screen.
          navigate('/onboarding/kyc/source-of-funds');
          break;
      }
    },
    [
      navigate,
      trackApiSuccess,
      isSsnBlocked,
      handleSsnBlocked,
      isWebViewActive,
      handleSendWebViewMessage,
      previousScreen,
    ],
  );

  useEffect(() => {
    if (dataWebSocket) {
      updateStatusKYC(dataWebSocket);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dataWebSocket]);

  return (
    <ViewModelContext.Provider
      value={{
        data,
        isConnected,
        sendMessage,
        disconnect,
      }}>
      {children}
    </ViewModelContext.Provider>
  );
}

export function useViewModelProvider() {
  const context = useContext(ViewModelContext);
  if (context === undefined) {
    throw new Error('KYC ViewModelContext is undefined');
  }
  return context;
}
